// according to https://codepulse.blog/simple-easy-way-display-draft-js-content/
// the best way to display content made in draftjs is just to show the editor in read only mode
// so that is what this is
import React, { useState, useMemo } from "react"
import { EditorState, convertFromRaw } from "draft-js"
import EmojiReactions from "./EmojiReactions"
import * as SocketIOClient from "socket.io-client"

import type { Comment, Reaction, Location, SystemInfo } from "../types"

function locationToHref(location: Location) {
	return `${location.host}/${JSON.parse(location.path).join("/")}`
}

function systemInfoToBrowserString(systemInfo: SystemInfo): string {
	let { browser, screenDimensions } = systemInfo
	let { name, version } = browser
	let { width, height } = screenDimensions
	return `${name} ${version} (${width}px x ${height}px)`
}

function systemInfoToOsString(systemInfo: SystemInfo): string {
	let { os } = systemInfo
	let { name, version } = os
	return `${name} ${version}`
}

export interface JamEditorDisplayModeProps {
	JAM_ID: string
	threadId: string
	commentId: string

	rawMessageBlocks: Draft.RawDraftContentState
	reactions: Reaction[]
	img?: string
	systemInfo?: SystemInfo
	location?: Location

	onCommentChange: (comment: Comment) => void

	socket: SocketIOClient.Socket
}

type ShowMode = "none" | "screenshot" | "info"

export default function JamEditorDisplayMode(props: JamEditorDisplayModeProps) {
	let [showMode, setShowMode] = useState<ShowMode>("none")

	let messageText = useMemo(() => {
		// the Editor component just fails sadly so this is a hack to just show unstyled text
		let state = EditorState.createWithContent(
			convertFromRaw(props.rawMessageBlocks),
		)
		return state.getCurrentContent().getPlainText("\u0001")
	}, [props.rawMessageBlocks])

	function handleToggleScreenshot() {
		setShowMode((mode) => {
			if (mode === "screenshot") {
				return "none"
			} else {
				return "screenshot"
			}
		})
	}

	function handleToggleInfo() {
		setShowMode((mode) => {
			if (mode === "info") {
				return "none"
			} else {
				return "info"
			}
		})
	}

	return (
		<div className="jam--comment-message">
			<p style={{}} className="jam--plain-text-message-text">
				{messageText}
			</p>
			<EmojiReactions
				reactions={props.reactions}
				JAM_ID={props.JAM_ID}
				threadId={props.threadId}
				commentId={props.commentId}
				onCommentChange={props.onCommentChange}
				socket={props.socket}
			/>
			{(props.img || props.systemInfo) && (
				<React.Fragment>
					<div className="jam--comment-options-section">
						{props.img && (
							<div
								style={{
									background: `${showMode === "screenshot" ? "#eee" : "#fff"}`,
								}}
								title="View screenshot"
								id="show-screenshot-button"
								onClick={handleToggleScreenshot}
								className="jam--comment-option"
							>
								<img
									id="show-screenshot-button"
									src="https://danigrant.github.io/img/screenshot.svg"
								/>
							</div>
						)}
						{props.systemInfo && (
							<div
								style={{
									background: `${showMode === "none" ? "#eee" : "#fff"}`,
								}}
								title="View system info"
								id="show-info-button"
								onClick={handleToggleInfo}
								className="jam--comment-option"
							>
								<img
									id="show-info-button"
									src="https://danigrant.github.io/img/info.svg"
								/>
							</div>
						)}
					</div>
					{showMode !== "none" && (
						<div className="jam--show-additional-section">
							{showMode === "screenshot" && props.img && (
								<img className="jam--screenshot" src={props.img} />
							)}
							{showMode === "info" && props.location && props.systemInfo && (
								<div>
									<p>{locationToHref(props.location)}</p>
									<p>{systemInfoToBrowserString(props.systemInfo)}</p>
									<p>{systemInfoToOsString(props.systemInfo)}</p>
								</div>
							)}
						</div>
					)}
					<style>{`
						.jam--comment-options-section {
							display: flex;
							width: 100%;
							box-sizing: border-box;
							padding: 0 1rem 1rem 1rem;
						}

						.jam--comment-option {
							width: 32px;
							height: 32px;
							border: 1px solid rgba(42, 54, 50, .1);
							box-sizing: border-box;
							border-radius: 50px;
							display: flex;
							align-items: center;
							justify-content: center;
							margin-right: 8px;
						}

						.jam--comment-option:hover {
							cursor: pointer;
							background: #eeeeee7a;
						}

						.with-padding .jam--comment-message .jam--comment-options-section {
							padding: 0.25rem 1rem 0rem 0rem !important;
						}

						.with-padding .jam--show-additional-section {
							padding: 1rem 0rem 0rem 0rem; !important;
						}

						.jam--show-additional-section {
							text-align: left;
							padding: 0rem 1rem 1rem 1rem;
						}

						.jam--show-additional-section p {
							margin: 1rem 0;
							font-size: 14px;
							font-weight: 400;
							color: #2A3632;
						}

						.jam--screenshot {
							width: 100%;
						}

						.jam--plain-text-message-text {
							margin: 1rem 0;
							font-size: 14px;
							font-weight: 400;
							color: #2A3632;
							word-break: break-all;
						}
					`}</style>
				</React.Fragment>
			)}
		</div>
	)
}
